import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import VerificationBadge from "@/components/VerificationBadge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Heart, MapPin, Wrench, Users } from "lucide-react";
import { Link } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { useFavoriteArtisans, useToggleFavoriteArtisan } from "@/hooks/useFavoriteArtisans";
import { useEffect } from "react";
import ClientBottomNav from "@/components/ClientBottomNav";
import { useSEO } from "@/hooks/useSEO";
import { useLanguage } from "@/hooks/useLanguage";
import { useLocalizedNavigate, useLocalizedPath } from "@/hooks/useLocalizedNavigate";

const FavorisArtisans = () => {
  useSEO({ noindex: true });
  const { t } = useLanguage("common");
  const lNavigate = useLocalizedNavigate();
  const lp = useLocalizedPath();
  const { user, loading: authLoading } = useAuth();
  const { data: favorites = [], isLoading } = useFavoriteArtisans();
  const toggleFavorite = useToggleFavoriteArtisan();

  useEffect(() => {
    if (!authLoading && !user) lNavigate("/connexion");
  }, [user, authLoading]);

  if (authLoading) return null;

  return (
    <div className="min-h-screen">
      <Navbar />
      <main className="container py-8">
        <div className="mb-8">
          <h1 className="font-display text-3xl font-bold">{t("favoris.title_artisans")}</h1>
          <p className="mt-1 text-muted-foreground">
            {t("favoris.count_artisans", { count: favorites.length })}
          </p>
        </div>

        {isLoading ? (
          <p className="text-center text-muted-foreground py-12">{t("favoris.loading")}</p>
        ) : favorites.length === 0 ? (
          <Card>
            <CardContent className="py-12 text-center">
              <Users className="mx-auto h-12 w-12 text-muted-foreground/30" />
              <p className="mt-3 text-muted-foreground">{t("favoris.no_artisans")}</p>
              <Link to={lp("/recherche-artisans")} className="mt-4 inline-block">
                <Button variant="outline">{t("favoris.browse_artisans")}</Button>
              </Link>
            </CardContent>
          </Card>
        ) : (
          <div className="grid gap-4 md:grid-cols-2">
            {favorites.map((fav) => {
              const artisan = fav.artisan;
              const profile = fav.profile;
              if (!artisan) return null;
              return (
                <Card key={fav.id} className="group overflow-hidden transition-all hover:shadow-lg hover:border-primary/20">
                  <CardContent className="flex gap-4 p-4">
                    <div className="flex h-16 w-16 shrink-0 items-center justify-center rounded-xl bg-secondary">
                      {artisan.logo_url ? (
                        <img src={artisan.logo_url} alt={profile?.display_name || ""} className="h-full w-full rounded-xl object-cover" />
                      ) : (
                        <Wrench className="h-8 w-8 text-muted-foreground" />
                      )}
                    </div>
                    <div className="flex-1 space-y-1.5 min-w-0">
                      <div className="flex items-start justify-between gap-2">
                        <Link to={lp(`/artisan/${fav.artisan_user_id}`)} className="min-w-0">
                          <h3 className="font-display font-semibold leading-tight truncate hover:text-primary transition-colors">
                            {profile?.display_name || "Artisan"}
                          </h3>
                        </Link>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="shrink-0"
                          onClick={() => toggleFavorite.mutate(fav.artisan_user_id)}
                        >
                          <Heart className="h-4 w-4 fill-destructive text-destructive" />
                        </Button>
                      </div>
                      <div className="flex flex-wrap items-center gap-2">
                        <VerificationBadge
                          type={artisan.type}
                          verificationStatus={(artisan as any).verification_status}
                          className="text-xs"
                        />
                        <span className="text-sm text-muted-foreground">{artisan.specialite}</span>
                      </div>
                      <div className="flex items-center gap-1 text-xs text-muted-foreground">
                        <MapPin className="h-3 w-3" />
                        <span>{artisan.commune}, {artisan.wilaya}</span>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}
      </main>
      <Footer />
      <ClientBottomNav />
    </div>
  );
};

export default FavorisArtisans;
